import React, { useRef, useCallback, useEffect } from 'react';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { appBundleReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import type { TimescaleQueryParam } from '@atlassian/jira-servicedesk-reports-common/src/common/helpers/types';
import Chart, {
	type RefInternal as ChartRef,
} from '@atlassian/jira-servicedesk-reports-common/src/ui/chart/main.tsx';
import OverallSummary from '@atlassian/jira-servicedesk-reports-common/src/ui/overall-summary';
import SummaryIssueTable from '@atlassian/jira-servicedesk-reports-common/src/ui/table';
import ErrorView from '@atlassian/jira-servicedesk-reports-common/src/ui/table/error';
import type { SeriesData } from '../services/summary/utils/types.tsx';
import { kbRequestsReportsPageLoad } from '../utils/performance-analytics';
import { APP_PAGINATION_DEFAULTS } from './constants';
import type { CommonProps } from './types';

export type Props = CommonProps & {
	reportId: string;
	dateFilterQuery: TimescaleQueryParam;
	selectedDrillDownPoint?: SeriesData;
	onSetDrillDownPoint: (seriesData?: SeriesData) => void;
};

const ChartWithSummaryDetails = ({
	summaryResource,
	drilldownResource,
	reportId,
	dateFilterQuery,
	selectedDrillDownPoint,
	onSetDrillDownPoint,
}: Props) => {
	const { data, loading, error } = summaryResource;
	const { fetch: fetchDrillDown } = drilldownResource;

	const ref = useRef<ChartRef>(null);

	useEffect(() => {
		if (selectedDrillDownPoint) {
			ref.current &&
				ref.current.highlightPoint({
					seriesIndex: selectedDrillDownPoint.seriesIndex,
					dataIndex: selectedDrillDownPoint.dataIndex,
					data: selectedDrillDownPoint,
				});

			const fetch = async () => {
				await fetchDrillDown({
					// @ts-expect-error - TS2783 - 'reportId' is specified more than once, so this usage will be overwritten.
					reportId,
					date: selectedDrillDownPoint.id,
					seriesId: selectedDrillDownPoint.seriesId,
					...dateFilterQuery,
					start: APP_PAGINATION_DEFAULTS.start,
					limit: APP_PAGINATION_DEFAULTS.limit,
				});
			};
			fetch();
		} else {
			ref.current && ref.current.downplayPoint();
		}
	}, [fetchDrillDown, dateFilterQuery, reportId, selectedDrillDownPoint]);

	const onDrilldownPageChange = useCallback(
		(page: number) => {
			if (selectedDrillDownPoint) {
				fetchDrillDown({
					// @ts-expect-error - TS2783 - 'reportId' is specified more than once, so this usage will be overwritten.
					reportId,
					date: selectedDrillDownPoint.id,
					seriesId: selectedDrillDownPoint.seriesId,
					...dateFilterQuery,
					start: (page - 1) * APP_PAGINATION_DEFAULTS.limit,
					limit: APP_PAGINATION_DEFAULTS.limit,
				});
			}
		},
		[fetchDrillDown, dateFilterQuery, reportId, selectedDrillDownPoint],
	);

	if (loading) {
		return <Spinner />;
	}

	if (error !== undefined || data === undefined) {
		return <ErrorView />;
	}

	return (
		<>
			<OverallSummary chartSeries={data.chartSeries} />
			<Chart
				source={data}
				ref={ref}
				onPointSelect={(pointEvent) => {
					onSetDrillDownPoint(pointEvent.data);
				}}
			/>
			<SummaryIssueTable
				summaryResource={summaryResource}
				drilldownResource={drilldownResource}
				selectedDrillDownPoint={selectedDrillDownPoint}
				onSetDrillDownPoint={onSetDrillDownPoint}
				onDrilldownPageChange={onDrilldownPageChange}
			/>
			<JsmPageInteractiveEvent
				key={reportId}
				metricKey="servicedesk.kb-requests-reports-v2"
				withMarks={[appBundleReadyMark]}
				appName="servicedesk.kb-requests-reports-v2"
				// @ts-expect-error - TS2322 - Type 'PageLoadMetric' is not assignable to type 'BM3Metric'.
				metric={kbRequestsReportsPageLoad}
			/>
		</>
	);
};

export default ChartWithSummaryDetails;
